// /home/Hakyury/sites/admin.eight-percent.com/frontend/src/contexts/AuthContext.js

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { 
  getTokens, 
  setTokens, 
  clearTokens, 
  getUserInfo,
  isManagerOrAdmin,
  extractTokensFromURL,
  getOAuthRedirectUrl,
  getOAuthLogoutUrl,
  initAuthTimers,
  ensureFreshAccess // Убедитесь, что эта функция экспортируется из authFetch.js
} from '../utils/authFetch'; // Исправить путь

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);


export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Функция для обновления токена
  const refreshAccessToken = useCallback(async () => {
    try {
      await ensureFreshAccess();
      const userInfo = getUserInfo();
      if (userInfo) {
        setUser({
          ...userInfo,
          hasAdminAccess: isManagerOrAdmin(),
          isAdmin: userInfo.isAdmin,
          isSuperAdmin: userInfo.isSuperAdmin,
          isManager: userInfo.isManager,
          role: userInfo.role,
          roleId: userInfo.roleId
        });
      }
      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return false;
    }
  }, []);
  
  // Инициализация авторизации
  const initAuth = useCallback(() => {
    setLoading(true);
    
    // Извлекаем токены из URL если есть
    extractTokensFromURL();
    
    const tokens = getTokens();
    if (tokens.access) {
      const userInfo = getUserInfo();
      if (userInfo) {
        const hasAdminAccess = isManagerOrAdmin();
        
        if (!hasAdminAccess) {
          console.warn('Пользователь не имеет прав доступа к админ-панели');
          // Для участников (роль 1) - редирект на главную сайта
          window.location.href = 'https://eight-percent.com';
          return;
        }
        
        setUser({
          ...userInfo,
          hasAdminAccess: true,
          isAdmin: userInfo.isAdmin,
          isSuperAdmin: userInfo.isSuperAdmin,
          isManager: userInfo.isManager,
          role: userInfo.role,
          roleId: userInfo.roleId
        });
        setIsAuthenticated(true);
          
        // Инициализируем таймеры для обновления токена
        initAuthTimers();
      } else {
        clearTokens();
        setUser(null);
        setIsAuthenticated(false);
      }
    }
    
    setLoading(false);
  }, []);

  useEffect(() => {
    initAuth();
    
    // Проверяем токен каждые 5 минут
    const interval = setInterval(() => {
      const tokens = getTokens();
      if (tokens.access) {
        refreshAccessToken().catch(() => {
          logout();
        });
      }
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [initAuth, refreshAccessToken]);

  const login = async (tokens) => {
    setTokens(tokens);
    
    // Даем время для записи в localStorage
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Получаем информацию о пользователе
    const userInfo = getUserInfo();
    if (!userInfo) {
      clearTokens();
      throw new Error('Не удалось получить информацию о пользователе');
    }
    
    // Проверяем права доступа
    const hasAdminAccess = isManagerOrAdmin();
    
    if (!hasAdminAccess) {
      clearTokens();
      // Для участников - редирект на главную сайта
      window.location.href = 'https://eight-percent.com';
      return;
    }
    
    setUser({
      ...userInfo,
      hasAdminAccess: true,
      isAdmin: userInfo.isAdmin,
      isSuperAdmin: userInfo.isSuperAdmin,
      isManager: userInfo.isManager,
      role: userInfo.role,
      roleId: userInfo.roleId
    });
    setIsAuthenticated(true);
    
    // Инициализируем таймеры
    initAuthTimers();
    
    return userInfo;
  };

  const logout = () => {
    clearTokens();
    setUser(null);
    setIsAuthenticated(false);
    // Выходим также из центральной OAuth-сессии, иначе следующий вход будет
    // автоматически восстановлен с токенами OAuth.
    window.location.replace(getOAuthLogoutUrl('https://eight-percent.com/'));
  };

  const checkAuth = async () => {
    const tokens = getTokens();
    if (!tokens.access) {
      logout();
      return false;
    }
    
    try {
      const userInfo = getUserInfo();
      const hasAdminAccess = isManagerOrAdmin();
      
      if (!userInfo || !hasAdminAccess) {
        // Для участников - редирект на главную сайта
        window.location.href = 'https://eight-percent.com';
        return false;
      }
      
      return true;
    } catch (error) {
      logout();
      return false;
    }
  };

  const redirectToOAuth = () => {
    window.location.href = getOAuthRedirectUrl();
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      login,
      logout,
      checkAuth,
      redirectToOAuth,
      refreshToken: refreshAccessToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};
